"use client";

import * as React from "react";
import { ChevronDown, ChevronUp, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * 접이식 구획 프리미티브 — UI 2.0 사이드바 공용 (JCG-2060 토대, B/D 에서 재사용).
 *
 * 헤더 클릭으로 접기/펼치기(아코디언). `fill` 이면 펼쳐졌을 때 남은 세로 공간을 채운다
 * (스택 안에서 지도처럼 큰 패널용). 색상은 전역 디자인 토큰만 사용하므로 다크모드 자동 대응.
 */
interface SectionProps {
  icon?: LucideIcon;
  /**
   * 헤더 라벨. 문자열 또는 라벨 전용 요소(예: 지도 라벨은 trail 쿼리 Suspense 경계를 스스로 가짐).
   * `ReactNode` 로 넓히지 않는다 — undefined/null 이 허용되면 접근성 이름 없는 접기 버튼이 컴파일된다.
   */
  label: string | React.ReactElement;
  open: boolean;
  onToggle: () => void;
  /** 헤더 우측(접힘 화살표 왼쪽)에 들어갈 배지 등 */
  right?: React.ReactNode;
  /** 펼쳐졌을 때 남은 세로 공간을 채움 (flex-1) */
  fill?: boolean;
  /** 아이콘을 브랜드 앰버로 강조 */
  amber?: boolean;
  className?: string;
  children: React.ReactNode;
}

export default function Section({
  icon: Icon,
  label,
  open,
  onToggle,
  right,
  fill,
  amber,
  className,
  children,
}: SectionProps) {
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border border-border bg-card",
        open && "shadow-sm",
        fill && open ? "min-h-0 flex-1" : "flex-none",
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "flex shrink-0 items-center gap-2 px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset",
          open ? "bg-accent" : "bg-transparent hover:bg-accent",
        )}
      >
        {Icon && (
          <Icon
            className={cn(
              "h-4 w-4 shrink-0",
              amber ? "text-amber-500" : "text-muted-foreground",
            )}
          />
        )}
        <span className="text-[13.5px] font-bold text-foreground">{label}</span>
        <span className="ml-auto flex items-center gap-2">
          {right}
          {open ? (
            <ChevronUp className="h-[15px] w-[15px] text-muted-foreground" />
          ) : (
            <ChevronDown className="h-[15px] w-[15px] text-muted-foreground" />
          )}
        </span>
      </button>
      {open && (
        <div
          className={cn(
            "flex flex-col px-3 pb-3 pt-0.5",
            fill ? "min-h-0 flex-1 overflow-hidden" : "overflow-y-auto",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
